'use client'

import { AlternateEmail, Cancel } from '@mui/icons-material'
import { Box, Chip } from '@mui/material'
import { useTranslations } from 'next-intl'

import type { FocusedContextRef } from '~/features/assistant/contextRef'
import assistantCategoryIcons from '~/features/assistant/mentionIcons'
import type { AssistantMentionCategory } from '~/features/assistant/useAssistantMentions'

import { contextRefKey } from './contextRefs'

type ContextRefChipsProps = {
  contextRefs: FocusedContextRef[]
  /** Offers to remove each entity; without it the chips are read-only. */
  onRemove?: (ref: FocusedContextRef) => void
  align?: 'flex-start' | 'flex-end'
  'data-testid'?: string
}

/** The entities tagged with `@`, as chips: in the composer before sending and on the message once sent. */
export default function ContextRefChips({
  contextRefs,
  onRemove,
  align = 'flex-start',
  'data-testid': testId
}: ContextRefChipsProps) {
  const t = useTranslations('features.AssistantBar')
  if (contextRefs.length === 0) return null
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: align }} data-testid={testId}>
      {contextRefs.map((ref) => (
        <Chip
          key={contextRefKey(ref)}
          icon={assistantCategoryIcons[ref.type as AssistantMentionCategory] ?? <AlternateEmail fontSize="small" />}
          label={ref.label}
          title={ref.label}
          size="small"
          variant="outlined"
          onDelete={onRemove ? () => onRemove(ref) : undefined}
          deleteIcon={<Cancel aria-label={t('removeContext')} />}
          sx={{ maxWidth: '100%' }}
        />
      ))}
    </Box>
  )
}
